// Clavian Boot's realtime messages (ADR-0063; spec/25 §13.5, §14; spec/09 §6).
//
// A boot server holds one socket, as principal `boot_server`, subscribed to its own channel `boot-server:{id}` and
// nothing else. Every frame either way is an `evt`:
//
//   api → boot server, on `boot-server:{id}`
//     boot.config.updated   fetch `GET /v1/boot-server/config` again
//     boot.wipe             force-wipe write-back or shader caches now
//     boot.edit.start       an edit was started: make the edit layer, and boot the PC into it (an edit boot) or attach
//                           it on the server on the game disk's letter (a server edit)
//     boot.edit.end         staff published or discarded an edit: close (or detach) the layer
//     boot.capture.start    a capture (or an import) is expected for a new image version
//     boot.wol.send         send Wake-on-LAN magic packets on the LAN (spec/25 §15)
//     boot.shader.promote   make a PC's shader-cache volume its GPU family's seed (spec/25 §11)
//     boot.server.update    staff pressed Update now: install the server's own downloaded, verified update (spec/25 §13.6)
//     boot.pc.identify      staff pressed Identify: the PC shows a large number on its own screen (spec/13 §3.9)
//
//   boot server → api (forwarded by the gateway, recorded through the outbox; spec/25 §13.5)
//     boot.server.status    version, disks, free space, licence state, RAM cache — every 60 s and on change
//     boot.pc.booted        a PC booted: what, how fast, where its writes go, Secure Boot
//     boot.metrics          a per-minute rollup per PC, and the server's uplink and disk queue
//     boot.job.progress     a capture, preparation, import, merge, shader wipe, shader seed or server edit moved on
//     boot.pc.state         a PC's live boot phase changed: network start seen, boot files sent, Windows loading, ready,
//                           failed, or woken but no network start seen (step D of ADR-0065; live only, never queued)
//     boot.pcs.summary      every ~30 s: the live phase of every PC it knows (live only, never queued)
//
//   api → console, on `venue:{id}:boot` (a member with `boot:view`)
//     boot.server.changed   a boot server's row changed
//     boot.pc.changed       a boot PC's row changed
//     boot.job.progress     the boot server's job progress, passed on
//
// The ones the api sends the boot server are courtesies: everything they say is also in the signed config (pending
// wipes, edits and captures), so a server that was offline when one was sent learns it on its next fetch. Two are not in
// the config and are transient: `boot.wol.send` (the station relay is the fallback, spec/25 §15), `boot.shader.promote`
// (sent only while the server is online; its result comes back in `boot.job.progress`) and `boot.server.update` (sent
// only while the server is online; its progress and result come back in `boot.server.status`).
import { z } from "../zod.js";
import { BootEditIdSchema, BootImageIdSchema, BootImageVersionIdSchema, BootPcIdSchema, BootShaderWipeIdSchema, StationIdSchema, } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { BootCpuNameSchema, BootDiskSchema, BootDriveLetterSchema, BootFolderModeSchema, BootIdSchema, BootEditTargetSchema, BootGpuFamilySchema, BootImageSourceSchema, BootLicenceStateSchema, BootMacSchema, BootMotherboardSchema, BootPciIdSchema, BootPcFailureSchema, BootPcPhaseSchema, BootPcSchema, BootServerSchema, BootServerUpdateResultSchema, BootServerUpdateStateSchema, BootServerVersionSchema, BootWipeKindSchema, BootWipeScopeSchema, BootWritebackLocationSchema, } from "../routes/boot.js";
import { BOOT_JOB_PROGRESS, BOOT_PC_CHANGED, BOOT_SERVER_CHANGED } from "./constants.js";
import { realtimeMessage } from "./envelope.js";
export { BOOT_JOB_PROGRESS, BOOT_PC_CHANGED, BOOT_SERVER_CHANGED };
export const BOOT_CONFIG_UPDATED = 'boot.config.updated';
/** `boot.wipe`: named for what it does, as `BOOT_WIPE` is the `boot:wipe` permission. */
export const BOOT_FORCE_WIPE = 'boot.wipe';
export const BOOT_EDIT_START = 'boot.edit.start';
export const BOOT_EDIT_END = 'boot.edit.end';
export const BOOT_CAPTURE_START = 'boot.capture.start';
export const BOOT_WOL_SEND = 'boot.wol.send';
export const BOOT_SHADER_PROMOTE = 'boot.shader.promote';
export const BOOT_SERVER_UPDATE = 'boot.server.update';
/** Staff pressed Identify on a new PC (spec/13 §3.9): transient, sent only while the server is online. */
export const BOOT_PC_IDENTIFY = 'boot.pc.identify';
export const BOOT_SERVER_STATUS = 'boot.server.status';
export const BOOT_PC_BOOTED = 'boot.pc.booted';
export const BOOT_METRICS = 'boot.metrics';
/** A PC's live boot phase changed (step D of ADR-0065, spec/25 §13.5). */
export const BOOT_PC_STATE = 'boot.pc.state';
/** Every ~30 s: the live phase of every PC the boot server knows (step D of ADR-0065, spec/25 §13.5). */
export const BOOT_PCS_SUMMARY = 'boot.pcs.summary';
/** What a boot server may send (spec/25 §13.5). The gateway forwards these and refuses anything else from one. */
export const BOOT_SERVER_EVENT_TYPES = [BOOT_SERVER_STATUS, BOOT_PC_BOOTED, BOOT_METRICS, BOOT_JOB_PROGRESS, BOOT_PC_STATE, BOOT_PCS_SUMMARY];
export function isBootServerEventType(value) {
    return typeof value === 'string' && BOOT_SERVER_EVENT_TYPES.includes(value);
}
// ---------------------------------------------------------------------------------------------
// api → boot server
// ---------------------------------------------------------------------------------------------
export const BootConfigUpdatedPayloadSchema = z
    .object({
    /** What changed, for the server's log: `pc`, `image`, `edit`, `wipe`, `settings`, `licence`… */
    reason: z.string().min(1).max(64),
    at: IsoDateTimeSchema,
})
    .openapi('BootConfigUpdatedPayload', { description: 'The boot config changed: fetch it again.' });
export const BootWipePayloadSchema = z
    .object({
    kind: BootWipeKindSchema,
    scope: BootWipeScopeSchema,
    /** `pc`: the PC's MAC. */
    mac: BootMacSchema.nullable(),
    /** `family`: the GPU family. */
    gpuFamily: BootGpuFamilySchema.nullable(),
    /** A shader-cache wipe's id, which the server reports done in `boot.job.progress`; null for write-back. */
    shaderWipeId: BootShaderWipeIdSchema.nullable(),
    reason: z.string().max(200),
    requestedAt: IsoDateTimeSchema,
})
    .openapi('BootWipePayload', { description: 'Force-wipe write-back or shader caches now (spec/25 §5.4, §11).' });
export const BootEditStartPayloadSchema = z
    .object({
    editId: BootEditIdSchema,
    imageId: BootImageIdSchema,
    /** The new, unpublished version to write into: `edit-<editId>.vhdx` on top of `parentVersionId`. */
    versionId: BootImageVersionIdSchema,
    versionNumber: z.int().min(1),
    parentVersionId: BootImageVersionIdSchema,
    /** `pc`: an edit boot; `server`: attach the layer on the server. Optional: absent is `pc` (an api built before). */
    target: BootEditTargetSchema.optional(),
    /** An edit boot's PC and its MAC; both null for a server edit. */
    bootPcId: BootPcIdSchema.nullable(),
    mac: BootMacSchema.nullable(),
    /** A server edit: the game disk's drive letter to mount the layer's data volume on. Null for an edit boot. */
    driveLetter: BootDriveLetterSchema.nullable().optional(),
})
    .openapi('BootEditStartPayload', {
    description: 'An edit started: make the edit layer; the PC’s next boot writes into it, or the server attaches it on the game disk’s letter.',
});
export const BootEditEndPayloadSchema = z
    .object({
    editId: BootEditIdSchema,
    imageId: BootImageIdSchema,
    versionId: BootImageVersionIdSchema,
    /** `publish`: close (a server edit: detach) the layer, run the light preparation, publish it; `discard`: delete it. */
    outcome: z.enum(['publish', 'discard']).meta({ id: 'BootEditEndOutcome' }),
    note: z.string().max(500).nullable(),
    /**
     * `publish` as a new image (“Save as a new image”, spec/25 §10): the layer becomes version `publishVersionNumber` (1)
     * of this image, in its folder, standing on its parent in `imageId`'s folder. Null or absent: a new version of
     * `imageId`. Optional: an api built before sends neither.
     */
    publishToImageId: BootImageIdSchema.nullable().optional(),
    publishVersionNumber: z.int().min(1).nullable().optional(),
})
    .openapi('BootEditEndPayload', { description: 'Staff ended an edit boot: publish or discard its layer.' });
export const BootCaptureStartPayloadSchema = z
    .object({
    imageId: BootImageIdSchema,
    versionId: BootImageVersionIdSchema,
    mode: BootImageSourceSchema,
    /** `capture`: SHA-256 (hex) of the one-time capture code the reference PC will present. */
    captureCodeSha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
    expiresAt: IsoDateTimeSchema.nullable(),
    /** `import`: the file on the server to import; `folder`: the drive or folder whose files make the game disk. */
    importPath: z.string().max(260).nullable(),
    /** `folder`: copy the files, or move them into the game disk (each deleted once its copy is checked). Absent is `copy`. */
    folderMode: BootFolderModeSchema.optional(),
})
    .openapi('BootCaptureStartPayload', { description: 'Expect a capture from a reference PC, or import a file, as a new image version.' });
export const BootWolSendPayloadSchema = z
    .object({
    /** The target's wired adapters' MACs. */
    macs: z.array(BootMacSchema).min(1).max(16),
    /** The station being woken, for the server's log. */
    stationId: StationIdSchema.nullable(),
    requestedAt: IsoDateTimeSchema,
})
    .openapi('BootWolSendPayload', { description: 'Send Wake-on-LAN magic packets to these MACs on the venue’s LAN (spec/25 §15).' });
export const BootShaderPromotePayloadSchema = z
    .object({
    /** The PC whose volume (`shader\<mac>.vhdx`) becomes the seed. It must be shut down. */
    mac: BootMacSchema,
    /** Its GPU family, as its last boot reported it: the seed is `shader\seed-<gpuFamily>.vhdx`. */
    gpuFamily: BootGpuFamilySchema,
    requestedAt: IsoDateTimeSchema,
})
    .openapi('BootShaderPromotePayload', { description: 'Make this PC’s shader-cache volume its GPU family’s seed (spec/25 §11).' });
export const BootServerUpdatePayloadSchema = z
    .object({
    /** The version to install: the one the server reported downloaded and verified. Anything else is refused. */
    version: BootServerVersionSchema,
    /**
     * Install even while PCs are booted from the server. Without it the server refuses while any PC has a live iSCSI
     * session, and reports how many in its status.
     */
    force: z.boolean(),
    requestedAt: IsoDateTimeSchema,
})
    .openapi('BootServerUpdatePayload', { description: 'Staff pressed Update now: install the server’s own downloaded, verified update (spec/25 §13.6).' });
export const BootPcIdentifyPayloadSchema = z
    .object({
    /** The PC, by its MAC. */
    mac: BootMacSchema,
    /** What it shows, large: the number it would be given ("14"). */
    label: z.string().min(1).max(12),
    /** It shows the label until this time. */
    until: IsoDateTimeSchema,
})
    .openapi('BootPcIdentifyPayload', {
    description: 'Staff pressed Identify: hand the PC this label to show full screen until `until` (through `whoami`, spec/25 §13.4).',
});
// ---------------------------------------------------------------------------------------------
// boot server → api
// ---------------------------------------------------------------------------------------------
export const BootServerStatusPayloadSchema = z
    .object({
    version: z.string().min(1).max(64),
    disks: z.array(BootDiskSchema).max(16),
    licenceState: BootLicenceStateSchema,
    ramCacheBytes: z.int().min(0),
    /** The `issuedAt` of the config it runs on; null before it has one. */
    configIssuedAt: IsoDateTimeSchema.nullable(),
    // Its own software update (F-BOOT-23, spec/25 §13.6). Optional: a server built before sends none of them.
    /** The MSI version installed (`major.minor.build.revision`). */
    installedVersion: BootServerVersionSchema.nullable().optional(),
    /** A newer release downloaded and verified, waiting for Update now; null when none. */
    availableVersion: BootServerVersionSchema.nullable().optional(),
    updateState: BootServerUpdateStateSchema.nullable().optional(),
    /** One line about the update state: what failed, why Update now was refused. */
    updateDetail: z.string().max(500).nullable().optional(),
    /** How the last install ended; null before any. */
    lastUpdate: BootServerUpdateResultSchema.nullable().optional(),
    /** PCs booted from it now (a live iSCSI session). */
    bootedPcs: z.int().min(0).nullable().optional(),
})
    .openapi('BootServerStatusPayload', { description: 'The boot server’s status, every 60 s and on change (spec/25 §13.5).' });
export const BootPcBootedPayloadSchema = z
    .object({
    mac: BootMacSchema,
    /** The station on the PC, when the station client has said (whoami). */
    stationId: StationIdSchema.nullable(),
    imageId: BootImageIdSchema.nullable(),
    versionId: BootImageVersionIdSchema.nullable(),
    versionNumber: z.int().min(1).nullable(),
    editBoot: z.boolean(),
    /** PXE request to the station's lock screen, when known. */
    bootSeconds: z.int().min(0).nullable(),
    writebackLocation: BootWritebackLocationSchema,
    /** Whether Secure Boot is on, as the PC's client reported it; null when no client reported (or it could not tell). */
    secureBoot: z.boolean().nullable(),
    gpuFamily: BootGpuFamilySchema.nullable(),
    nicPciIds: z.array(BootPciIdSchema).max(16),
    at: IsoDateTimeSchema,
    /**
     * The boot server's id for this boot: a UUID it mints at a new iSCSI login (never a reinstated session) and keeps for
     * the boot. A boot attestation names it (spec/10 §14.2). Optional: a server built before sends none.
     */
    bootId: BootIdSchema.optional(),
    /** The processor's name as the PC's client reported it; null when no client reported. Optional, an addition. */
    cpu: BootCpuNameSchema.nullable().optional(),
    /**
     * The PC's motherboard (SMBIOS baseboard): from iPXE's request for its boot script, before Windows starts, else from the
     * PC's client; null when neither said. Optional, an addition (ADR-0065 decision 8).
     */
    motherboard: BootMotherboardSchema.nullable().optional(),
    /**
     * Whether the boot reached Windows: true when the PC's Clavian Boot client reported its status; false when the server
     * reports the boot on its own after five minutes with no word from the PC. Optional, an addition.
     */
    reachedWindows: z.boolean().optional(),
    /**
     * The game disk's drive letter on the PC this boot and whether its volume took it (spec/25 §12), from the client's
     * status; absent when not reported. Optional, additions (step D of ADR-0065): the Floor says when another drive of the
     * PC holds the letter.
     */
    gameDiskLetter: BootDriveLetterSchema.nullable().optional(),
    gameDiskLetterAssigned: z.boolean().nullable().optional(),
})
    .openapi('BootPcBootedPayload', { description: 'A PC booted from the boot server (spec/25 §13.5). An unknown MAC is a PC the server auto-added.' });
const BOOT_METRIC_SAMPLE = {
    readOps: z.int().min(0),
    p50Us: z.int().min(0),
    p99Us: z.int().min(0),
    hitPct: z.number().min(0).max(100),
    bytes: z.int().min(0),
};
/** One PC's minute: reads, p50/p99 read latency in microseconds, cache hits (0–100) and bytes. */
export const BootPcMetricSampleSchema = z.object({ mac: BootMacSchema, ...BOOT_METRIC_SAMPLE }).meta({ id: 'BootPcMetricSample' });
/** The server's own minute: the same, and its uplink use (0–100) and disk queue. */
export const BootServerMetricSampleSchema = z
    .object({ ...BOOT_METRIC_SAMPLE, uplinkPct: z.number().min(0).max(100), diskQueue: z.number().min(0) })
    .meta({ id: 'BootServerMetricSample' });
export const BootMetricsPayloadSchema = z
    .object({
    /** The minute this rollup is for (its start, UTC). */
    minute: IsoDateTimeSchema,
    pcs: z.array(BootPcMetricSampleSchema).max(1000),
    server: BootServerMetricSampleSchema,
})
    .openapi('BootMetricsPayload', { description: 'One minute of boot health: per PC, and the server’s own (spec/25 §13.5).' });
/**
 * What kind of job a `boot.job.progress` is about. `import` also covers a game disk made from a folder (its files copied
 * in). `server_edit` is a game disk's server edit being opened (spec/25 §12): `running` once its new version is mounted
 * on the game disk's letter (the message says where), `failed` when the server could not open it (the message says why:
 * the letter is in use…); its publish is reported as `preparation`, as any edit's is.
 */
export const BOOT_JOBS = ['capture', 'preparation', 'import', 'merge', 'shader_wipe', 'shader_seed', 'server_edit'];
export const BootJobSchema = z.enum(BOOT_JOBS).openapi('BootJob', {
    description: 'A boot server job: a capture, a preparation, an import, a merge, a shader-cache wipe, a shader-cache seed promotion, or a game disk’s server edit being opened.',
});
export const BOOT_JOB_STATES = ['running', 'succeeded', 'failed'];
export const BootJobStateSchema = z.enum(BOOT_JOB_STATES).openapi('BootJobState', { description: 'Whether a boot server job is running, succeeded or failed.' });
export const BootJobProgressPayloadSchema = z
    .object({
    job: BootJobSchema,
    state: BootJobStateSchema,
    imageId: BootImageIdSchema.nullable(),
    /** The version the job is for: a capture, preparation or import's new version, or a merge's `upToVersionId`. */
    versionId: BootImageVersionIdSchema.nullable(),
    shaderWipeId: BootShaderWipeIdSchema.nullable(),
    /** 0–100, when the job can tell. */
    percent: z.int().min(0).max(100).nullable(),
    /** One line for staff: the step it is on, or why it failed. */
    message: z.string().max(500).nullable(),
    /** A finished capture, import or preparation: the version's size. */
    sizeBytes: z.int().min(0).nullable(),
    // Optional: a server built before sends neither.
    /** A running capture: the bytes received so far. */
    bytesDone: z.int().min(0).nullable().optional(),
    /** A running capture: the bytes it expects in all (the used space of the volumes it captures). */
    bytesTotal: z.int().min(0).nullable().optional(),
    /** A finished preparation: the report of every step and what it changed (spec/25 §9.2). */
    preparationReport: z.looseObject({}).nullable(),
    at: IsoDateTimeSchema,
    /** A capture (any state): the reference PC's motherboard, as its capture app reported it. Optional, an addition. */
    motherboard: BootMotherboardSchema.nullable().optional(),
})
    .openapi('BootJobProgressPayload', { description: 'A boot server job moved on (spec/25 §13.5). Passed on to the console as it is.' });
export const BootPcStatePayloadSchema = z
    .object({
    mac: BootMacSchema,
    phase: BootPcPhaseSchema,
    /** Since when it has been in this phase. */
    since: IsoDateTimeSchema,
    /** The boot's id once the iSCSI login gave it one (`loading_windows` onwards); absent before. */
    bootId: BootIdSchema.optional(),
    /** `failed` only: why. */
    reason: BootPcFailureSchema.optional(),
    at: IsoDateTimeSchema,
})
    .openapi('BootPcStatePayload', {
    description: 'A PC’s live boot phase changed (spec/25 §13.5). Sent live only, at most one a second per PC (the latest wins), never queued while the link is down.',
});
export const BootPcsSummaryPayloadSchema = z
    .object({
    /** Every PC the boot server knows with a live phase other than `off`; a PC left out is off. */
    pcs: z
        .array(z
        .object({ mac: BootMacSchema, phase: BootPcPhaseSchema, since: IsoDateTimeSchema, bootId: BootIdSchema.optional(), reason: BootPcFailureSchema.optional() })
        .meta({ id: 'BootPcsSummaryRow' }))
        .max(1000),
    at: IsoDateTimeSchema,
})
    .openapi('BootPcsSummaryPayload', {
    description: 'Every ~30 s: the live phase of every PC the boot server knows (spec/25 §13.5). The cloud takes it as the whole truth, and a server that stops sending it makes its PCs’ states unknown.',
});
// ---------------------------------------------------------------------------------------------
// api → console, on `venue:{id}:boot`
// ---------------------------------------------------------------------------------------------
export const BootServerChangedPayloadSchema = z
    .object({ server: BootServerSchema })
    .openapi('BootServerChangedPayload', { description: 'A boot server changed: its status, version, disks or licence state.' });
export const BootPcChangedPayloadSchema = z
    .object({ pc: BootPcSchema })
    .openapi('BootPcChangedPayload', { description: 'A boot PC changed: it booted, was added, assigned or removed.' });
// ---------------------------------------------------------------------------------------------
// The framed messages
// ---------------------------------------------------------------------------------------------
export const BootConfigUpdatedMessage = realtimeMessage('evt', BOOT_CONFIG_UPDATED, BootConfigUpdatedPayloadSchema);
export const BootWipeMessage = realtimeMessage('evt', BOOT_FORCE_WIPE, BootWipePayloadSchema);
export const BootEditStartMessage = realtimeMessage('evt', BOOT_EDIT_START, BootEditStartPayloadSchema);
export const BootEditEndMessage = realtimeMessage('evt', BOOT_EDIT_END, BootEditEndPayloadSchema);
export const BootCaptureStartMessage = realtimeMessage('evt', BOOT_CAPTURE_START, BootCaptureStartPayloadSchema);
export const BootWolSendMessage = realtimeMessage('evt', BOOT_WOL_SEND, BootWolSendPayloadSchema);
export const BootShaderPromoteMessage = realtimeMessage('evt', BOOT_SHADER_PROMOTE, BootShaderPromotePayloadSchema);
export const BootServerUpdateMessage = realtimeMessage('evt', BOOT_SERVER_UPDATE, BootServerUpdatePayloadSchema);
export const BootPcIdentifyMessage = realtimeMessage('evt', BOOT_PC_IDENTIFY, BootPcIdentifyPayloadSchema);
export const BootServerStatusMessage = realtimeMessage('evt', BOOT_SERVER_STATUS, BootServerStatusPayloadSchema);
export const BootPcBootedMessage = realtimeMessage('evt', BOOT_PC_BOOTED, BootPcBootedPayloadSchema);
export const BootMetricsMessage = realtimeMessage('evt', BOOT_METRICS, BootMetricsPayloadSchema);
export const BootJobProgressMessage = realtimeMessage('evt', BOOT_JOB_PROGRESS, BootJobProgressPayloadSchema);
export const BootPcStateMessage = realtimeMessage('evt', BOOT_PC_STATE, BootPcStatePayloadSchema);
export const BootPcsSummaryMessage = realtimeMessage('evt', BOOT_PCS_SUMMARY, BootPcsSummaryPayloadSchema);
export const BootServerChangedMessage = realtimeMessage('evt', BOOT_SERVER_CHANGED, BootServerChangedPayloadSchema);
export const BootPcChangedMessage = realtimeMessage('evt', BOOT_PC_CHANGED, BootPcChangedPayloadSchema);
/** The payload schema for each event a boot server sends: the gateway validates against it before forwarding. */
export const BOOT_SERVER_EVENT_PAYLOADS = {
    [BOOT_SERVER_STATUS]: BootServerStatusPayloadSchema,
    [BOOT_PC_BOOTED]: BootPcBootedPayloadSchema,
    [BOOT_METRICS]: BootMetricsPayloadSchema,
    [BOOT_JOB_PROGRESS]: BootJobProgressPayloadSchema,
    [BOOT_PC_STATE]: BootPcStatePayloadSchema,
    [BOOT_PCS_SUMMARY]: BootPcsSummaryPayloadSchema,
};
//# sourceMappingURL=boot.js.map